import { pickT } from "@repo/shared";
import type { ClientAccessImpact, ClientAccessSettings } from "@repo/shared/types";
import { T } from "./client-access-consequences.i18n";

/**
 * QL-380 — the sentences for switching to the `target` settings. Pure wording:
 * which client lands in which audience is decided server-side (the impact
 * endpoint applies the access rule to the target combination); this only turns
 * the buckets into text, picking the phrasing that fits the switches that are on.
 */

export type ClientAccessConsequence = { tone: "info" | "warning"; text: string };

type Plural<A> = { one: (a: A) => string; other: (a: A) => string };
type ByCombination = { private: Plural<string>; team: Plural<string>; both: Plural<string> };

const pick = <A>(n: number, p: Plural<A>) => (n === 1 ? p.one : p.other);

export function describeClientAccessConsequences(
  target: ClientAccessSettings,
  /** Counts computed by the server for `target`. */
  impact: ClientAccessImpact,
  lang: string,
): ClientAccessConsequence[] {
  const t = pickT(T, lang);
  const clients = (n: number) => pick(n, t.clients)(n);
  const say = (n: number, p: Plural<string>) => pick(n, p)(clients(n));
  const out: ClientAccessConsequence[] = [];
  const { privateEnabled, teamAccessEnabled } = target;

  if (teamAccessEnabled && impact.teamCount === 0) {
    out.push({ tone: "warning", text: t.teamsNone });
  }
  if (impact.totalClients === 0) {
    out.push({ tone: "info", text: t.noClients });
    return out;
  }

  if (!privateEnabled && !teamAccessEnabled) {
    const n = impact.visibleToAll;
    out.push({ tone: "info", text: pick(n, t.everyone)(n) });
    return out;
  }

  const { visibleToAll, teamLimited, ownerOnly, adminComplianceOnly } = impact;
  const combination: keyof ByCombination =
    privateEnabled && teamAccessEnabled ? "both" : privateEnabled ? "private" : "team";

  if (combination === "private" && ownerOnly + adminComplianceOnly === 0) {
    out.push({ tone: "info", text: t.privateNone });
    return out;
  }

  if (teamLimited > 0) out.push({ tone: "info", text: say(teamLimited, t.teamLimited) });
  // With team access on and no teams at all, the headline warning already says "owner-only".
  if (ownerOnly > 0 && !(teamAccessEnabled && impact.teamCount === 0)) {
    out.push({
      // Private clients are owner-only by choice; under team access it may be an oversight.
      tone: combination === "private" ? "info" : "warning",
      text: say(ownerOnly, t.ownerOnly[combination]),
    });
  }
  if (adminComplianceOnly > 0) {
    out.push({
      tone: "warning",
      text: say(adminComplianceOnly, t.adminComplianceOnly[combination]),
    });
  }
  if (visibleToAll > 0) out.push({ tone: "info", text: say(visibleToAll, t.visibleToAll) });
  return out;
}
